import type { StaffPermission } from '../staff/staff-authority.ts';

export interface TableDefinition {
  readonly tableId: string;
  readonly label: string;
  readonly areaId: string;
  readonly capacity: number;
  readonly position: number;
  readonly enabled: boolean;
  readonly mergeWith: readonly string[];
  readonly temporaryParentId?: string;
}

export interface QueueClassDefinition {
  readonly classId: string;
  readonly prefix: string;
  readonly minPartySize: number;
  readonly maxPartySize: number;
  readonly minDigits?: number;
}

export type QueueEntryStatus = 'waiting' | 'called' | 'seated' | 'left' | 'cancelled';

export interface QueueEntrySnapshot {
  readonly queueEntryId: string;
  readonly originGuestGroupId: string;
  readonly businessDate: string;
  readonly queueClassId: string;
  readonly displayCode: string;
  readonly sequence: number;
  readonly partySize: number;
  readonly itemCount: number;
  readonly status: QueueEntryStatus;
  readonly orderIds: readonly string[];
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface SeatingGroupSnapshot {
  readonly seatingGroupId: string;
  readonly originGuestGroupId: string;
  readonly partySize: number;
  readonly createdAt: string;
}

export type TableSessionStatus = 'occupied' | 'paid_continuing' | 'closed';

export interface TableSessionSnapshot {
  readonly sessionId: string;
  readonly seatingGroupId: string;
  readonly originGuestGroupId: string;
  readonly partySize: number;
  readonly tableIds: readonly string[];
  readonly orderIds: readonly string[];
  readonly status: TableSessionStatus;
  readonly openedAt: string;
  readonly updatedAt: string;
  readonly closedAt?: string;
  readonly queueEntryId?: string;
  readonly joinedSeatingGroupIds: readonly string[];
  readonly joinedOriginGuestGroupIds: readonly string[];
  readonly joinedIntoSessionId?: string;
  readonly closedReason?: 'joined';
}

export interface TableProjectionEntry extends TableDefinition {
  readonly state: 'available' | 'occupied' | 'disabled' | 'partitioned';
  readonly activeSessionId?: string;
}

export interface TableAuthorityProjection {
  readonly storeId: string;
  readonly businessDate: string;
  readonly revision: number;
  readonly tables: readonly TableProjectionEntry[];
  readonly queueEntries: readonly QueueEntrySnapshot[];
  readonly seatingGroups: readonly SeatingGroupSnapshot[];
  readonly sessions: readonly TableSessionSnapshot[];
}

export interface TableMutationContext {
  readonly expectedRevision: number;
  readonly idempotencyKey: string;
  readonly actorId: string;
  readonly at: string;
}

interface OperationalFactsLike {
  append(input: {
    readonly eventId: string;
    readonly aggregateType: string;
    readonly aggregateId: string;
    readonly factType: string;
    readonly businessDate: string;
    readonly occurredAt: string;
    readonly recordedAt: string;
    readonly schemaVersion: number;
    readonly source: { readonly authority: string; readonly revision: string };
    readonly payload: unknown;
  }): unknown;
}

export interface CreateTableAuthorityOptions {
  readonly storeId: string;
  readonly businessDate: string;
  readonly tables: readonly TableDefinition[];
  readonly queueClasses: readonly QueueClassDefinition[];
  readonly authorize: (request: {
    readonly actorId: string;
    readonly permission: StaffPermission;
    readonly at: string;
  }) => void;
  readonly isOrderFullySettled: (orderId: string) => boolean;
  readonly facts: OperationalFactsLike;
}

interface IdempotentResult {
  readonly signature: string;
  readonly result: unknown;
}

const clone = <T>(value: T): T => structuredClone(value);

const stableValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(stableValue);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, entry]) => [key, stableValue(entry)]),
    );
  }
  return value;
};

const signatureOf = (value: unknown): string => JSON.stringify(stableValue(value));

const assertText = (value: string, code: string): void => {
  if (!value?.trim()) throw new Error(code);
};

const assertTime = (value: string): void => {
  if (!Number.isFinite(Date.parse(value))) throw new Error('TABLE_TIMESTAMP_INVALID');
};

const assertBusinessDate = (value: string): void => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) throw new Error('TABLE_BUSINESS_DATE_INVALID');
};

const assertPositiveInteger = (value: number, code: string): void => {
  if (!Number.isSafeInteger(value) || value <= 0) throw new Error(code);
};

const normalizeStringList = (values: readonly string[], duplicateCode: string): string[] => {
  const normalized = values.map((value) => value.trim());
  if (normalized.some((value) => !value)) throw new Error('TABLE_REFERENCE_REQUIRED');
  if (new Set(normalized).size !== normalized.length) throw new Error(duplicateCode);
  return normalized;
};

export function createTableAuthority(options: CreateTableAuthorityOptions) {
  assertText(options.storeId, 'TABLE_STORE_ID_REQUIRED');
  assertBusinessDate(options.businessDate);

  let businessDate = options.businessDate;
  let revision = 0;
  const tables = new Map<string, TableDefinition>();
  const partitionChildren = new Map<string, string[]>();
  const queueClasses = new Map<string, QueueClassDefinition>();
  const queueCounters = new Map<string, number>();
  const queueEntries = new Map<string, QueueEntrySnapshot>();
  const seatingGroups = new Map<string, SeatingGroupSnapshot>();
  const sessions = new Map<string, TableSessionSnapshot>();
  const idempotency = new Map<string, IdempotentResult>();

  for (const input of options.tables) {
    assertText(input.tableId, 'TABLE_ID_REQUIRED');
    assertText(input.label, 'TABLE_LABEL_REQUIRED');
    assertText(input.areaId, 'TABLE_AREA_REQUIRED');
    assertPositiveInteger(input.capacity, 'TABLE_CAPACITY_INVALID');
    if (!Number.isSafeInteger(input.position)) throw new Error('TABLE_POSITION_INVALID');
    if (tables.has(input.tableId)) throw new Error('TABLE_ID_DUPLICATE');
    tables.set(input.tableId, {
      ...clone(input),
      tableId: input.tableId.trim(),
      label: input.label.trim(),
      areaId: input.areaId.trim(),
      mergeWith: normalizeStringList(input.mergeWith, 'TABLE_MERGE_REFERENCE_DUPLICATE'),
    });
  }
  for (const table of tables.values()) {
    for (const related of table.mergeWith) {
      if (!tables.has(related)) throw new Error(`TABLE_MERGE_REFERENCE_NOT_FOUND:${related}`);
    }
  }

  const classRanges: QueueClassDefinition[] = [];
  for (const input of options.queueClasses) {
    assertText(input.classId, 'TABLE_QUEUE_CLASS_ID_REQUIRED');
    assertText(input.prefix, 'TABLE_QUEUE_PREFIX_REQUIRED');
    assertPositiveInteger(input.minPartySize, 'TABLE_QUEUE_PARTY_RANGE_INVALID');
    assertPositiveInteger(input.maxPartySize, 'TABLE_QUEUE_PARTY_RANGE_INVALID');
    if (input.minPartySize > input.maxPartySize) throw new Error('TABLE_QUEUE_PARTY_RANGE_INVALID');
    if (input.minDigits !== undefined && (!Number.isSafeInteger(input.minDigits) || input.minDigits <= 0)) {
      throw new Error('TABLE_QUEUE_DIGITS_INVALID');
    }
    if (queueClasses.has(input.classId)) throw new Error('TABLE_QUEUE_CLASS_DUPLICATE');
    const accepted = clone(input);
    for (const existing of classRanges) {
      const overlap = accepted.minPartySize <= existing.maxPartySize && accepted.maxPartySize >= existing.minPartySize;
      if (overlap) throw new Error('TABLE_QUEUE_CLASS_RANGE_OVERLAP');
    }
    classRanges.push(accepted);
    queueClasses.set(accepted.classId, accepted);
    queueCounters.set(accepted.classId, 0);
  }

  const activeSessionForTable = (tableId: string): TableSessionSnapshot | null => {
    for (const session of sessions.values()) {
      if (session.status !== 'closed' && session.tableIds.includes(tableId)) return session;
    }
    return null;
  };

  const tableState = (table: TableDefinition): Pick<TableProjectionEntry, 'state' | 'activeSessionId'> => {
    if (!table.enabled) return { state: 'disabled' };
    if (partitionChildren.has(table.tableId)) return { state: 'partitioned' };
    const active = activeSessionForTable(table.tableId);
    return active ? { state: 'occupied', activeSessionId: active.sessionId } : { state: 'available' };
  };

  const projection = (): TableAuthorityProjection => ({
    storeId: options.storeId,
    businessDate,
    revision,
    tables: [...tables.values()]
      .sort((left, right) => left.position - right.position || left.tableId.localeCompare(right.tableId))
      .map((table) => ({ ...clone(table), ...tableState(table) })),
    queueEntries: [...queueEntries.values()]
      .sort((left, right) => left.createdAt.localeCompare(right.createdAt) || left.queueEntryId.localeCompare(right.queueEntryId))
      .map(clone),
    seatingGroups: [...seatingGroups.values()].map(clone),
    sessions: [...sessions.values()].map(clone),
  });

  const assertContext = (context: TableMutationContext): void => {
    if (!Number.isSafeInteger(context.expectedRevision) || context.expectedRevision < 0) {
      throw new Error('TABLE_EXPECTED_REVISION_INVALID');
    }
    assertText(context.idempotencyKey, 'TABLE_IDEMPOTENCY_REQUIRED');
    assertText(context.actorId, 'TABLE_ACTOR_REQUIRED');
    assertTime(context.at);
  };

  const emitFact = (
    factType: string,
    aggregateId: string,
    context: TableMutationContext,
    acceptedRevision: number,
    payload: unknown,
    suffix = '',
  ): void => {
    options.facts.append({
      eventId: `table:${options.storeId}:${context.idempotencyKey}${suffix ? `:${suffix}` : ''}`,
      aggregateType: factType.startsWith('queue.') ? 'queue_entry' : 'table_session',
      aggregateId,
      factType,
      businessDate,
      occurredAt: context.at,
      recordedAt: context.at,
      schemaVersion: 1,
      source: { authority: 'table', revision: String(acceptedRevision) },
      payload,
    });
  };

  const mutate = <T>(
    kind: string,
    input: unknown,
    context: TableMutationContext,
    permission: StaffPermission | readonly StaffPermission[],
    action: (acceptedRevision: number) => T,
  ): T => {
    assertContext(context);
    const signature = signatureOf({ kind, input, context: { ...context, expectedRevision: undefined } });
    const replay = idempotency.get(context.idempotencyKey);
    if (replay) {
      if (replay.signature !== signature) throw new Error('TABLE_IDEMPOTENCY_CONFLICT');
      return clone(replay.result as T);
    }
    if (context.expectedRevision !== revision) throw new Error('TABLE_VERSION_CONFLICT');
    for (const requiredPermission of (Array.isArray(permission) ? permission : [permission]) as readonly StaffPermission[]) {
      options.authorize({ actorId: context.actorId, permission: requiredPermission, at: context.at });
    }
    const acceptedRevision = revision + 1;
    const result = action(acceptedRevision);
    revision = acceptedRevision;
    idempotency.set(context.idempotencyKey, { signature, result: clone(result) });
    return clone(result);
  };

  const requireTable = (tableId: string): TableDefinition => {
    const table = tables.get(tableId);
    if (!table) throw new Error('TABLE_NOT_FOUND');
    return table;
  };

  const requireAvailable = (tableId: string, ignoredSessionId?: string): TableDefinition => {
    const table = requireTable(tableId);
    if (!table.enabled || partitionChildren.has(tableId)) throw new Error('TABLE_NOT_AVAILABLE');
    const active = activeSessionForTable(tableId);
    if (active && active.sessionId !== ignoredSessionId) throw new Error('TABLE_NOT_AVAILABLE');
    return table;
  };

  const assertConnectedCluster = (tableIds: readonly string[]): string[] => {
    const ids = normalizeStringList(tableIds, 'TABLE_CLUSTER_DUPLICATE');
    if (ids.length === 0) throw new Error('TABLE_CLUSTER_REQUIRED');
    ids.forEach((id) => requireTable(id));
    if (ids.length === 1) return ids;

    const allowed = new Set(ids);
    const visited = new Set<string>();
    const queue = [ids[0]!];
    while (queue.length > 0) {
      const current = queue.shift()!;
      if (visited.has(current)) continue;
      visited.add(current);
      const table = requireTable(current);
      const neighbors = new Set<string>(table.mergeWith);
      for (const other of tables.values()) {
        if (other.mergeWith.includes(current)) neighbors.add(other.tableId);
      }
      for (const neighbor of neighbors) {
        if (allowed.has(neighbor) && !visited.has(neighbor)) queue.push(neighbor);
      }
    }
    if (visited.size !== allowed.size) throw new Error('TABLE_CLUSTER_NOT_MERGEABLE');
    return ids;
  };

  const requireSession = (sessionId: string): TableSessionSnapshot => {
    const session = sessions.get(sessionId);
    if (!session) throw new Error('TABLE_SESSION_NOT_FOUND');
    return session;
  };

  const activeWaitingList = (): QueueEntrySnapshot[] => [...queueEntries.values()]
    .filter((entry) => entry.businessDate === businessDate && (entry.status === 'waiting' || entry.status === 'called'))
    .sort((left, right) => left.createdAt.localeCompare(right.createdAt) || left.sequence - right.sequence)
    .map(clone);

  const assertFullySettled = (session: TableSessionSnapshot): void => {
    if (session.orderIds.some((orderId) => !options.isOrderFullySettled(orderId))) {
      throw new Error('TABLE_SESSION_PAYMENT_OUTSTANDING');
    }
  };

  const openSession = (
    input: {
      readonly sessionId: string;
      readonly seatingGroupId: string;
      readonly originGuestGroupId: string;
      readonly partySize: number;
      readonly tableIds: readonly string[];
      readonly orderIds?: readonly string[];
      readonly queueEntryId?: string;
    },
    context: TableMutationContext,
  ): TableSessionSnapshot => {
    assertText(input.sessionId, 'TABLE_SESSION_ID_REQUIRED');
    assertText(input.seatingGroupId, 'TABLE_SEATING_GROUP_ID_REQUIRED');
    assertText(input.originGuestGroupId, 'TABLE_ORIGIN_GUEST_GROUP_ID_REQUIRED');
    assertPositiveInteger(input.partySize, 'TABLE_PARTY_SIZE_INVALID');
    if (sessions.has(input.sessionId)) throw new Error('TABLE_SESSION_ID_CONFLICT');
    const tableIds = assertConnectedCluster(input.tableIds);
    tableIds.forEach((tableId) => requireAvailable(tableId));
    const orderIds = normalizeStringList(input.orderIds ?? [], 'TABLE_ORDER_ID_DUPLICATE');

    const existingGroup = seatingGroups.get(input.seatingGroupId);
    if (existingGroup && (
      existingGroup.originGuestGroupId !== input.originGuestGroupId || existingGroup.partySize !== input.partySize
    )) throw new Error('TABLE_SEATING_GROUP_CONFLICT');
    if (!existingGroup) {
      seatingGroups.set(input.seatingGroupId, {
        seatingGroupId: input.seatingGroupId,
        originGuestGroupId: input.originGuestGroupId,
        partySize: input.partySize,
        createdAt: context.at,
      });
    }

    const snapshot: TableSessionSnapshot = {
      sessionId: input.sessionId,
      seatingGroupId: input.seatingGroupId,
      originGuestGroupId: input.originGuestGroupId,
      partySize: input.partySize,
      tableIds,
      orderIds,
      status: 'occupied',
      openedAt: context.at,
      updatedAt: context.at,
      ...(input.queueEntryId ? { queueEntryId: input.queueEntryId } : {}),
      joinedSeatingGroupIds: [input.seatingGroupId],
      joinedOriginGuestGroupIds: [input.originGuestGroupId],
    };
    sessions.set(snapshot.sessionId, snapshot);
    return snapshot;
  };

  return {
    projection(): TableAuthorityProjection {
      return clone(projection());
    },

    session(sessionId: string): TableSessionSnapshot | null {
      const current = sessions.get(sessionId);
      return current ? clone(current) : null;
    },

    createQueueEntry(
      input: { readonly queueEntryId: string; readonly originGuestGroupId: string; readonly partySize: number; readonly itemCount: number },
      context: TableMutationContext,
    ): QueueEntrySnapshot {
      return mutate('queue.create', input, context, 'table.open', (acceptedRevision) => {
        assertText(input.queueEntryId, 'TABLE_QUEUE_ENTRY_ID_REQUIRED');
        assertText(input.originGuestGroupId, 'TABLE_ORIGIN_GUEST_GROUP_ID_REQUIRED');
        assertPositiveInteger(input.partySize, 'TABLE_PARTY_SIZE_INVALID');
        if (!Number.isSafeInteger(input.itemCount) || input.itemCount < 0) throw new Error('TABLE_QUEUE_ITEM_COUNT_INVALID');
        if (queueEntries.has(input.queueEntryId)) throw new Error('TABLE_QUEUE_ENTRY_ID_CONFLICT');
        const queueClass = [...queueClasses.values()].find((entry) =>
          input.partySize >= entry.minPartySize && input.partySize <= entry.maxPartySize,
        );
        if (!queueClass) throw new Error('TABLE_QUEUE_CLASS_NOT_FOUND');
        const sequence = (queueCounters.get(queueClass.classId) ?? 0) + 1;
        queueCounters.set(queueClass.classId, sequence);
        const displayCode = `${queueClass.prefix}${String(sequence).padStart(queueClass.minDigits ?? 3, '0')}`;
        const snapshot: QueueEntrySnapshot = {
          queueEntryId: input.queueEntryId,
          originGuestGroupId: input.originGuestGroupId,
          businessDate,
          queueClassId: queueClass.classId,
          displayCode,
          sequence,
          partySize: input.partySize,
          itemCount: input.itemCount,
          status: 'waiting',
          orderIds: [],
          createdAt: context.at,
          updatedAt: context.at,
        };
        queueEntries.set(snapshot.queueEntryId, snapshot);
        emitFact('queue.entry.created', snapshot.queueEntryId, context, acceptedRevision, {
          displayCode, partySize: snapshot.partySize, queueClassId: snapshot.queueClassId,
        });
        return snapshot;
      });
    },

    cancelQueueEntry(queueEntryId: string, context: TableMutationContext): QueueEntrySnapshot {
      return mutate('queue.cancel', { queueEntryId }, context, 'table.open', (acceptedRevision) => {
        const entry = queueEntries.get(queueEntryId);
        if (!entry) throw new Error('TABLE_QUEUE_ENTRY_NOT_FOUND');
        if (entry.status === 'seated') throw new Error('TABLE_QUEUE_ALREADY_SEATED');
        const snapshot = { ...entry, status: 'cancelled' as const, updatedAt: context.at };
        queueEntries.set(queueEntryId, snapshot);
        emitFact('queue.entry.cancelled', queueEntryId, context, acceptedRevision, { displayCode: entry.displayCode });
        return snapshot;
      });
    },

    callQueueEntry(queueEntryId: string, context: TableMutationContext): QueueEntrySnapshot {
      return mutate('queue.call', { queueEntryId }, context, 'table.open', (acceptedRevision) => {
        const entry = queueEntries.get(queueEntryId);
        if (!entry) throw new Error('TABLE_QUEUE_ENTRY_NOT_FOUND');
        if (entry.status !== 'waiting' && entry.status !== 'called') throw new Error('TABLE_QUEUE_ENTRY_NOT_WAITING');
        const snapshot = { ...entry, status: 'called' as const, updatedAt: context.at };
        queueEntries.set(queueEntryId, snapshot);
        emitFact('queue.entry.called', queueEntryId, context, acceptedRevision, { displayCode: entry.displayCode });
        return snapshot;
      });
    },

    markQueueEntryLeft(queueEntryId: string, context: TableMutationContext): QueueEntrySnapshot {
      return mutate('queue.left', { queueEntryId }, context, 'table.open', (acceptedRevision) => {
        const entry = queueEntries.get(queueEntryId);
        if (!entry) throw new Error('TABLE_QUEUE_ENTRY_NOT_FOUND');
        if (entry.status === 'seated' || entry.status === 'cancelled' || entry.status === 'left') {
          throw new Error('TABLE_QUEUE_ENTRY_NOT_WAITING');
        }
        const snapshot = { ...entry, status: 'left' as const, updatedAt: context.at };
        queueEntries.set(queueEntryId, snapshot);
        emitFact('queue.entry.left', queueEntryId, context, acceptedRevision, { displayCode: entry.displayCode });
        return snapshot;
      });
    },

    rollQueueBusinessDay(
      input: { readonly nextBusinessDate: string; readonly previousBusinessDayClosed: boolean },
      context: TableMutationContext,
    ): { readonly businessDate: string; readonly revision: number } {
      return mutate('queue.roll_business_day', input, context, 'table.open', (acceptedRevision) => {
        assertBusinessDate(input.nextBusinessDate);
        if (!input.previousBusinessDayClosed) throw new Error('TABLE_PREVIOUS_BUSINESS_DAY_OPEN');
        if (input.nextBusinessDate === businessDate) throw new Error('TABLE_BUSINESS_DATE_UNCHANGED');
        businessDate = input.nextBusinessDate;
        for (const classId of queueCounters.keys()) queueCounters.set(classId, 0);
        emitFact('queue.business_day.rolled', options.storeId, context, acceptedRevision, { nextBusinessDate: businessDate });
        return { businessDate, revision: acceptedRevision };
      });
    },

    attachWaitingOrder(queueEntryId: string, orderId: string, context: TableMutationContext): QueueEntrySnapshot {
      return mutate('queue.attach_order', { queueEntryId, orderId }, context, 'table.open', (acceptedRevision) => {
        assertText(orderId, 'TABLE_ORDER_ID_REQUIRED');
        const entry = queueEntries.get(queueEntryId);
        if (!entry) throw new Error('TABLE_QUEUE_ENTRY_NOT_FOUND');
        if (entry.status === 'seated' || entry.status === 'left' || entry.status === 'cancelled') {
          throw new Error('TABLE_QUEUE_ENTRY_NOT_ACTIVE');
        }
        if (entry.orderIds.includes(orderId)) throw new Error('TABLE_ORDER_ALREADY_ATTACHED');
        const snapshot = { ...entry, orderIds: [...entry.orderIds, orderId], updatedAt: context.at };
        queueEntries.set(queueEntryId, snapshot);
        emitFact('queue.order.attached', queueEntryId, context, acceptedRevision, { orderId });
        return snapshot;
      });
    },

    seatQueueEntry(
      input: { readonly queueEntryId: string; readonly sessionId: string; readonly seatingGroupId: string; readonly tableIds: readonly string[] },
      context: TableMutationContext,
    ): TableSessionSnapshot {
      return mutate('queue.seat', input, context, 'table.open', (acceptedRevision) => {
        const entry = queueEntries.get(input.queueEntryId);
        if (!entry) throw new Error('TABLE_QUEUE_ENTRY_NOT_FOUND');
        if (entry.status !== 'waiting' && entry.status !== 'called') throw new Error('TABLE_QUEUE_ENTRY_NOT_WAITING');
        const session = openSession({
          sessionId: input.sessionId,
          seatingGroupId: input.seatingGroupId,
          originGuestGroupId: entry.originGuestGroupId,
          partySize: entry.partySize,
          tableIds: input.tableIds,
          orderIds: entry.orderIds,
          queueEntryId: entry.queueEntryId,
        }, context);
        queueEntries.set(entry.queueEntryId, { ...entry, status: 'seated', updatedAt: context.at });
        emitFact('queue.entry.seated', entry.queueEntryId, context, acceptedRevision, { sessionId: session.sessionId }, 'queue');
        emitFact('table.session.opened', session.sessionId, context, acceptedRevision, { tableIds: session.tableIds, queueEntryId: entry.queueEntryId }, 'session');
        return session;
      });
    },

    openWalkInSession(
      input: { readonly sessionId: string; readonly seatingGroupId: string; readonly originGuestGroupId: string; readonly partySize: number; readonly tableIds: readonly string[] },
      context: TableMutationContext,
    ): TableSessionSnapshot {
      return mutate('session.open_walk_in', input, context, 'table.open', (acceptedRevision) => {
        const session = openSession(input, context);
        emitFact('table.session.opened', session.sessionId, context, acceptedRevision, { tableIds: session.tableIds, partySize: session.partySize });
        return session;
      });
    },

    attachOrder(sessionId: string, orderId: string, context: TableMutationContext): TableSessionSnapshot {
      return mutate('session.attach_order', { sessionId, orderId }, context, 'table.open', (acceptedRevision) => {
        assertText(orderId, 'TABLE_ORDER_ID_REQUIRED');
        const current = requireSession(sessionId);
        if (current.status === 'closed') throw new Error('TABLE_SESSION_CLOSED');
        if (current.orderIds.includes(orderId)) throw new Error('TABLE_ORDER_ALREADY_ATTACHED');
        const snapshot: TableSessionSnapshot = {
          ...current,
          orderIds: [...current.orderIds, orderId],
          status: 'occupied',
          updatedAt: context.at,
        };
        sessions.set(sessionId, snapshot);
        emitFact('table.order.attached', sessionId, context, acceptedRevision, { orderId });
        return snapshot;
      });
    },

    transferSession(sessionId: string, tableIds: readonly string[], context: TableMutationContext): TableSessionSnapshot {
      return mutate('session.transfer', { sessionId, tableIds }, context, 'table.transfer', (acceptedRevision) => {
        const current = requireSession(sessionId);
        if (current.status === 'closed') throw new Error('TABLE_SESSION_CLOSED');
        const acceptedTableIds = assertConnectedCluster(tableIds);
        acceptedTableIds.forEach((tableId) => requireAvailable(tableId, sessionId));
        const snapshot = { ...current, tableIds: acceptedTableIds, updatedAt: context.at };
        sessions.set(sessionId, snapshot);
        emitFact('table.session.transferred', sessionId, context, acceptedRevision, {
          fromTableIds: current.tableIds,
          toTableIds: acceptedTableIds,
        });
        return snapshot;
      });
    },

    mergeSessions(
      input: { readonly primarySessionId: string; readonly secondarySessionIds: readonly string[] },
      context: TableMutationContext,
    ): TableSessionSnapshot {
      return mutate('session.merge', input, context, 'table.merge', (acceptedRevision) => {
        const primary = requireSession(input.primarySessionId);
        if (primary.status === 'closed') throw new Error('TABLE_SESSION_CLOSED');
        const secondaryIds = normalizeStringList(input.secondarySessionIds, 'TABLE_SESSION_MERGE_DUPLICATE');
        if (secondaryIds.length === 0 || secondaryIds.includes(primary.sessionId)) throw new Error('TABLE_SESSION_MERGE_REQUIRED');
        const secondary = secondaryIds.map((sessionId) => requireSession(sessionId));
        if (secondary.some((session) => session.status === 'closed')) throw new Error('TABLE_SESSION_CLOSED');
        const tableIds = assertConnectedCluster([...primary.tableIds, ...secondary.flatMap((session) => session.tableIds)]);
        const orderIds = normalizeStringList([...primary.orderIds, ...secondary.flatMap((session) => session.orderIds)], 'TABLE_ORDER_ID_DUPLICATE');
        const joinedSeatingGroupIds = [...new Set([...primary.joinedSeatingGroupIds, ...secondary.flatMap((session) => session.joinedSeatingGroupIds)])];
        const joinedOriginGuestGroupIds = [...new Set([...primary.joinedOriginGuestGroupIds, ...secondary.flatMap((session) => session.joinedOriginGuestGroupIds)])];
        const merged: TableSessionSnapshot = {
          ...primary,
          partySize: primary.partySize + secondary.reduce((sum, session) => sum + session.partySize, 0),
          tableIds,
          orderIds,
          joinedSeatingGroupIds,
          joinedOriginGuestGroupIds,
          updatedAt: context.at,
        };
        sessions.set(primary.sessionId, merged);
        for (const session of secondary) {
          sessions.set(session.sessionId, {
            ...session,
            status: 'closed',
            closedAt: context.at,
            updatedAt: context.at,
            joinedIntoSessionId: primary.sessionId,
            closedReason: 'joined',
          });
        }
        emitFact('table.session.joined', primary.sessionId, context, acceptedRevision, {
          secondarySessionIds: secondaryIds, tableIds, orderIds,
        });
        return merged;
      });
    },

    rearrangeAndOpenSession(
      input: {
        readonly moves: readonly { readonly sessionId: string; readonly toTableIds: readonly string[] }[];
        readonly newSession: {
          readonly sessionId: string;
          readonly seatingGroupId: string;
          readonly originGuestGroupId: string;
          readonly partySize: number;
          readonly tableIds: readonly string[];
          readonly queueEntryId?: string;
        };
      },
      context: TableMutationContext,
    ): { readonly movedSessions: readonly TableSessionSnapshot[]; readonly newSession: TableSessionSnapshot } {
      const permissions: readonly StaffPermission[] = input.moves.length > 0 ? ['table.transfer', 'table.open'] : ['table.open'];
      return mutate('session.rearrange_and_open', input, context, permissions, (acceptedRevision) => {
        assertText(input.newSession.sessionId, 'TABLE_SESSION_ID_REQUIRED');
        assertText(input.newSession.seatingGroupId, 'TABLE_SEATING_GROUP_ID_REQUIRED');
        assertText(input.newSession.originGuestGroupId, 'TABLE_ORIGIN_GUEST_GROUP_ID_REQUIRED');
        assertPositiveInteger(input.newSession.partySize, 'TABLE_PARTY_SIZE_INVALID');
        if (sessions.has(input.newSession.sessionId)) throw new Error('TABLE_SESSION_ID_CONFLICT');

        const moveIds = normalizeStringList(input.moves.map((move) => move.sessionId), 'TABLE_REARRANGEMENT_SESSION_DUPLICATE');
        const movingSessions = input.moves.map((move) => {
          const session = requireSession(move.sessionId);
          if (session.status === 'closed') throw new Error('TABLE_SESSION_CLOSED');
          return session;
        });
        const movingSessionIds = new Set(moveIds);
        const reserved = new Set<string>();
        const acceptedMoves = input.moves.map((move, index) => {
          const toTableIds = assertConnectedCluster(move.toTableIds);
          for (const tableId of toTableIds) {
            const table = requireTable(tableId);
            if (!table.enabled || partitionChildren.has(tableId)) throw new Error('TABLE_NOT_AVAILABLE');
            const active = activeSessionForTable(tableId);
            if (active && !movingSessionIds.has(active.sessionId)) throw new Error('TABLE_NOT_AVAILABLE');
            if (reserved.has(tableId)) throw new Error('TABLE_REARRANGEMENT_DESTINATION_CONFLICT');
            reserved.add(tableId);
          }
          return { session: movingSessions[index]!, toTableIds };
        });

        const newTableIds = assertConnectedCluster(input.newSession.tableIds);
        for (const tableId of newTableIds) {
          const table = requireTable(tableId);
          if (!table.enabled || partitionChildren.has(tableId)) throw new Error('TABLE_NOT_AVAILABLE');
          const active = activeSessionForTable(tableId);
          if (active && !movingSessionIds.has(active.sessionId)) throw new Error('TABLE_NOT_AVAILABLE');
          if (reserved.has(tableId)) throw new Error('TABLE_REARRANGEMENT_DESTINATION_CONFLICT');
        }

        let queueEntry: QueueEntrySnapshot | undefined;
        if (input.newSession.queueEntryId) {
          queueEntry = queueEntries.get(input.newSession.queueEntryId);
          if (!queueEntry) throw new Error('TABLE_QUEUE_ENTRY_NOT_FOUND');
          if (queueEntry.status !== 'waiting' && queueEntry.status !== 'called') throw new Error('TABLE_QUEUE_ENTRY_NOT_WAITING');
          if (queueEntry.originGuestGroupId !== input.newSession.originGuestGroupId || queueEntry.partySize !== input.newSession.partySize) {
            throw new Error('TABLE_QUEUE_SEATING_MISMATCH');
          }
        }

        const existingGroup = seatingGroups.get(input.newSession.seatingGroupId);
        if (existingGroup && (existingGroup.originGuestGroupId !== input.newSession.originGuestGroupId || existingGroup.partySize !== input.newSession.partySize)) {
          throw new Error('TABLE_SEATING_GROUP_CONFLICT');
        }

        const movedSessions = acceptedMoves.map(({ session, toTableIds }) => {
          const moved = { ...session, tableIds: toTableIds, updatedAt: context.at };
          sessions.set(session.sessionId, moved);
          return moved;
        });
        if (!existingGroup) {
          seatingGroups.set(input.newSession.seatingGroupId, {
            seatingGroupId: input.newSession.seatingGroupId,
            originGuestGroupId: input.newSession.originGuestGroupId,
            partySize: input.newSession.partySize,
            createdAt: context.at,
          });
        }
        const created: TableSessionSnapshot = {
          sessionId: input.newSession.sessionId,
          seatingGroupId: input.newSession.seatingGroupId,
          originGuestGroupId: input.newSession.originGuestGroupId,
          partySize: input.newSession.partySize,
          tableIds: newTableIds,
          orderIds: queueEntry?.orderIds ? [...queueEntry.orderIds] : [],
          status: 'occupied',
          openedAt: context.at,
          updatedAt: context.at,
          ...(queueEntry ? { queueEntryId: queueEntry.queueEntryId } : {}),
          joinedSeatingGroupIds: [input.newSession.seatingGroupId],
          joinedOriginGuestGroupIds: [input.newSession.originGuestGroupId],
        };
        sessions.set(created.sessionId, created);
        if (queueEntry) {
          queueEntries.set(queueEntry.queueEntryId, { ...queueEntry, status: 'seated', updatedAt: context.at });
        }
        emitFact('table.rearrangement.applied', created.sessionId, context, acceptedRevision, {
          moves: movedSessions.map((session) => ({ sessionId: session.sessionId, toTableIds: session.tableIds })),
          newSessionId: created.sessionId, newTableIds: created.tableIds,
        });
        return { movedSessions, newSession: created };
      });
    },

    splitOriginGuestGroup(
      input: { readonly originGuestGroupId: string; readonly groups: readonly { readonly seatingGroupId: string; readonly partySize: number }[] },
      context: TableMutationContext,
    ): readonly SeatingGroupSnapshot[] {
      return mutate('guest_group.split', input, context, 'table.split', (acceptedRevision) => {
        assertText(input.originGuestGroupId, 'TABLE_ORIGIN_GUEST_GROUP_ID_REQUIRED');
        if (input.groups.length < 2) throw new Error('TABLE_SPLIT_GROUPS_REQUIRED');
        const seen = new Set<string>();
        const created = input.groups.map((group) => {
          assertText(group.seatingGroupId, 'TABLE_SEATING_GROUP_ID_REQUIRED');
          assertPositiveInteger(group.partySize, 'TABLE_PARTY_SIZE_INVALID');
          if (seen.has(group.seatingGroupId) || seatingGroups.has(group.seatingGroupId)) {
            throw new Error('TABLE_SEATING_GROUP_ID_CONFLICT');
          }
          seen.add(group.seatingGroupId);
          return {
            seatingGroupId: group.seatingGroupId,
            originGuestGroupId: input.originGuestGroupId,
            partySize: group.partySize,
            createdAt: context.at,
          } satisfies SeatingGroupSnapshot;
        });
        created.forEach((group) => seatingGroups.set(group.seatingGroupId, group));
        emitFact('guest_group.split', input.originGuestGroupId, context, acceptedRevision, {
          groups: created.map(({ seatingGroupId, partySize }) => ({ seatingGroupId, partySize })),
        });
        return created;
      });
    },

    markPaidContinuing(sessionId: string, context: TableMutationContext): TableSessionSnapshot {
      return mutate('session.paid_continuing', { sessionId }, context, 'table.clear', (acceptedRevision) => {
        const current = requireSession(sessionId);
        if (current.status === 'closed') throw new Error('TABLE_SESSION_CLOSED');
        assertFullySettled(current);
        const snapshot = { ...current, status: 'paid_continuing' as const, updatedAt: context.at };
        sessions.set(sessionId, snapshot);
        emitFact('table.session.paid_continuing', sessionId, context, acceptedRevision, { orderIds: snapshot.orderIds });
        return snapshot;
      });
    },

    payAndClose(sessionId: string, context: TableMutationContext): {
      readonly session: TableSessionSnapshot;
      readonly waitingList: readonly QueueEntrySnapshot[];
    } {
      return mutate('session.pay_and_close', { sessionId }, context, 'table.clear', (acceptedRevision) => {
        const current = requireSession(sessionId);
        if (current.status === 'closed') throw new Error('TABLE_SESSION_CLOSED');
        assertFullySettled(current);
        const snapshot: TableSessionSnapshot = {
          ...current,
          status: 'closed',
          updatedAt: context.at,
          closedAt: context.at,
        };
        sessions.set(sessionId, snapshot);
        const waitingList = activeWaitingList();
        emitFact('table.session.closed', sessionId, context, acceptedRevision, {
          tableIds: snapshot.tableIds,
          waitingQueueEntryIds: waitingList.map((entry) => entry.queueEntryId),
        });
        return { session: snapshot, waitingList };
      });
    },

    partitionTable(
      input: { readonly parentTableId: string; readonly children: readonly { readonly tableId: string; readonly label: string; readonly capacity: number; readonly position: number }[] },
      context: TableMutationContext,
    ): readonly TableProjectionEntry[] {
      return mutate('table.partition', input, context, 'table.split', (acceptedRevision) => {
        const parent = requireAvailable(input.parentTableId);
        if (partitionChildren.has(parent.tableId)) throw new Error('TABLE_ALREADY_PARTITIONED');
        if (input.children.length < 2) throw new Error('TABLE_PARTITION_CHILDREN_REQUIRED');
        const childIds = new Set<string>();
        for (const child of input.children) {
          assertText(child.tableId, 'TABLE_ID_REQUIRED');
          assertText(child.label, 'TABLE_LABEL_REQUIRED');
          assertPositiveInteger(child.capacity, 'TABLE_CAPACITY_INVALID');
          if (!Number.isSafeInteger(child.position)) throw new Error('TABLE_POSITION_INVALID');
          if (tables.has(child.tableId) || childIds.has(child.tableId)) throw new Error('TABLE_ID_CONFLICT');
          childIds.add(child.tableId);
        }
        const ids = [...childIds];
        for (const child of input.children) {
          tables.set(child.tableId, {
            tableId: child.tableId,
            label: child.label,
            areaId: parent.areaId,
            capacity: child.capacity,
            position: child.position,
            enabled: true,
            mergeWith: ids.filter((id) => id !== child.tableId),
            temporaryParentId: parent.tableId,
          });
        }
        partitionChildren.set(parent.tableId, ids);
        emitFact('table.partitioned', parent.tableId, context, acceptedRevision, { childTableIds: ids });
        return projection().tables.filter((table) => table.tableId === parent.tableId || ids.includes(table.tableId));
      });
    },

    collapsePartition(parentTableId: string, context: TableMutationContext): TableProjectionEntry {
      return mutate('table.collapse_partition', { parentTableId }, context, 'table.split', (acceptedRevision) => {
        const parent = requireTable(parentTableId);
        const children = partitionChildren.get(parentTableId);
        if (!children) throw new Error('TABLE_NOT_PARTITIONED');
        for (const childId of children) {
          if (activeSessionForTable(childId)) throw new Error('TABLE_PARTITION_CHILD_OCCUPIED');
        }
        for (const childId of children) tables.delete(childId);
        partitionChildren.delete(parentTableId);
        emitFact('table.partition.collapsed', parentTableId, context, acceptedRevision, { childTableIds: children });
        return { ...clone(parent), ...tableState(parent) };
      });
    },
  };
}
